import React, { useMemo } from 'react';
import { useCapabilityMetrics } from 'js/hooks/useCapabilityMetrics';
import CapabilityResultBar from './CapabilityResultBar';
import LegendItem from './LegendItem';
import { buildCapabilitySetsFromCache } from 'js/ydoc/docs/capabilities-assessment/capability-utils';
import { getPageCapabilities } from 'js/engines/CapabilitiesAssessment/utils';

import styles from './CapabilityResultsChart.module.scss';

const CapabilityResultsChart = ({
  userGroupAssessment,
  capabilityElementMap,
}) => {
  const userGroupAssessments = useMemo(
    () => (userGroupAssessment ? [userGroupAssessment] : []),
    [userGroupAssessment]
  );

  const { capabilities, capabilitySets, capabilityElements, metrics } =
    useCapabilityMetrics(userGroupAssessments);

  const capabilityElementObjects = Object.values(capabilityElements);
  const assessmentMetrics = metrics[userGroupAssessment.id];
  const results = Object.entries(capabilities).reduce(
    (acc, [capabilityId, capability]) => {
      const capabilitySetId = capability.capability_set_id;
      if (!(capabilitySetId in acc)) {
        acc[capabilitySetId] = {
          ...capabilitySets[capabilitySetId],
          capabilities: [],
        };
      }

      const elementMetrics = capabilityElementObjects.reduce((ceAcc, ce) => {
        const capabilityMetricForElement =
          assessmentMetrics.metrics?.[ce.key]?.[capabilityId];

        if (capabilityMetricForElement) {
          ceAcc[ce.key] = {
            elementKey: ce.key,
            answerDomain: capabilityMetricForElement.answerDomain,
            normalizedScore: capabilityMetricForElement.normalizedScore,
            minScore: capabilityMetricForElement.minScore,
            maxScore: capabilityMetricForElement.maxScore,
          };
        }
        return ceAcc;
      }, {});

      acc[capabilitySetId].capabilities.push({
        ...capability,
        elementMetrics,
      });

      return acc;
    },
    {}
  );

  const capabilityElementMapObjects = Object.values(capabilityElementMap).sort(
    (a, b) => a.paletteIndex - b.paletteIndex
  );

  // Match the order the assessment presents capabilities in: page by page,
  // walking each capability set's tree
  const { document } = userGroupAssessment.assessment;
  const cachedCapabilitySets = buildCapabilitySetsFromCache(
    document.capabilitySetCache ?? {},
    document.capabilityCache ?? {}
  );
  let capabilityIndex = 0;
  const capabilityOrder = document.pages.reduce((acc, page) => {
    const capabilitySet = cachedCapabilitySets[page.capabilitySetId];
    if (page.type !== 'capability-questions' || !capabilitySet) {
      return acc;
    }
    getPageCapabilities(capabilitySet, page.selectedCapabilities ?? {}).forEach(
      (c) => {
        if (!(c.id in acc)) {
          acc[c.id] = capabilityIndex++;
        }
      }
    );
    return acc;
  }, {});
  Object.values(results).forEach((cs) => {
    cs.capabilities.sort(
      (a, b) =>
        (capabilityOrder[a.id] ?? Infinity) -
          (capabilityOrder[b.id] ?? Infinity) || 0
    );
  });

  return (
    <div className="mt-2">
      <label>Quick Overview</label>
      <div>
        {Object.values(results).map((cs) => (
          <div key={cs.id} className={styles['capability-set']}>
            <div className={styles['capability-set-name']}>{cs.name}</div>
            <div className={styles['capability-set-capabilities']}>
              {cs.capabilities.map((c) => (
                <div key={c.id} className={styles['capability']}>
                  {/* <div style={{ color: 'red' }}>{c.name}</div> */}
                  <div className={styles['capability-name']}>
                    <div className={styles['capability-name-text']}>
                      {c.name}
                    </div>
                  </div>
                  <div className={styles['capability-bar']}>
                    <CapabilityResultBar
                      segmentWidth={40}
                      height={100}
                      elementMetrics={c.elementMetrics}
                      capabilityElementMap={capabilityElementMap}
                      capability={c}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className={styles['legend']}>
          {capabilityElementMapObjects.map((ce) => (
            <LegendItem key={ce.key} capabilityElement={ce} />
          ))}
        </div>
      </div>
    </div>
  );
};

export default CapabilityResultsChart;
